import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import LoadingSpinner from '../components/common/LoadingSpinner';
import useAuth from '../hooks/useAuth';
import { consumePostLoginRedirect, setToken } from '../services/authService';

/**
 * Extrahiert das JWT aus dem URL-Fragment (Form: "#token=<jwt>").
 * Liefert null, wenn kein (nicht-leeres) Token vorhanden ist.
 */
function extractTokenFromHash(hash: string): string | null {
  const fragment = hash.startsWith('#') ? hash.slice(1) : hash;
  const token = new URLSearchParams(fragment).get('token');
  return token !== null && token !== '' ? token : null;
}

/**
 * OAuth-Callback-Seite (/auth/callback#token=<jwt>).
 *
 * Ablauf: Token aus dem Fragment lesen, in der sessionStorage speichern,
 * das Fragment aus der URL entfernen (damit das JWT nicht in History/
 * Bookmarks landet) und den Benutzer per GET /api/auth/me validieren.
 * Erfolg -> gemerkter Deep-Link (sonst Dashboard), Fehler -> /login?error=oauth.
 */
function AuthCallbackPage(): React.JSX.Element {
  const navigate = useNavigate();
  const { logout, refresh } = useAuth();
  // Verhindert doppelte Verarbeitung durch den doppelten
  // Effect-Aufruf im React.StrictMode (Development)
  const processedRef = useRef<boolean>(false);
  // Nur navigieren, solange der Effect noch "aktiv" ist (Cleanup setzt
  // das Flag zurueck). Bewusst als Ref statt lokaler Effect-Variable:
  // Im React.StrictMode wird der erste Effect-Durchlauf sofort wieder
  // aufgeraeumt, waehrend processedRef den zweiten Durchlauf ueberspringt;
  // der zweite Durchlauf reaktiviert die Ref, sodass die Navigation des
  // ersten (einzigen) refresh()-Aufrufs trotzdem stattfindet.
  const isActiveRef = useRef<boolean>(false);

  useEffect(() => {
    isActiveRef.current = true;
    const markInactive = (): void => {
      isActiveRef.current = false;
    };

    if (processedRef.current) {
      return markInactive;
    }
    processedRef.current = true;

    const token = extractTokenFromHash(window.location.hash);
    if (token === null) {
      // Kein Token im Fragment (fehlgeschlagener OAuth-Flow): Der
      // Provider-Mount ueberspringt auf /auth/callback die Validierung,
      // daher wird der Auth-Zustand hier explizit per logout() aufgeloest.
      logout();
      navigate('/login?error=oauth', { replace: true });
      return markInactive;
    }

    setToken(token);
    window.history.replaceState(null, '', window.location.pathname + window.location.search);

    void refresh().then((success) => {
      if (!isActiveRef.current) {
        // Komponente inzwischen entfernt -> keine Navigation mehr ausloesen
        return;
      }
      if (success) {
        // Deep-Link-Erhalt: gemerkten internen Pfad ansteuern, sonst Dashboard
        navigate(consumePostLoginRedirect() ?? '/', { replace: true });
      } else {
        navigate('/login?error=oauth', { replace: true });
      }
    });

    return markInactive;
  }, [logout, navigate, refresh]);

  return (
    <div
      id="auth-callback-page"
      className="d-flex flex-column align-items-center justify-content-center min-vh-100 px-3"
    >
      <LoadingSpinner id="auth-callback-spinner" />
      <p id="auth-callback-text" className="text-muted mt-3 mb-0">
        Anmeldung wird abgeschlossen &hellip;
      </p>
    </div>
  );
}

export default AuthCallbackPage;
